/* eslint-disable @typescript-eslint/camelcase */
import Vue from "vue";
import Vuex from "vuex";
import { StudioState } from "@/interfaces/studioState";
import { WidgetConfiguration } from "@/interfaces/widgetConfiguration";
import { Layout } from "@/interfaces/layout";
import { WidgetPayload } from "@/interfaces/widgetPayload";
import { WidgetState } from "@/interfaces/widgetState";
import { ParticipantInfo, User, UserPreferences } from "@/interfaces/user";
import { StudioAttributes } from "@/interfaces/studioAttributes";
import { Script } from "@/interfaces/script";
import { QuickConnectCode } from "@/interfaces/quickConnectCode";
import BackgroundSettings from "@/interfaces/studioBackground";
import { ErrorReportService } from "@/services/errorReportService";

Vue.use(Vuex);

interface VibrixStore {
    studio: StudioState | null;
    user: User | null;
    userPreferences: UserPreferences | null;
    snackbar: {
        text: string;
        color: string;
        value: boolean;
        trigger: boolean;
        timeout: number;
    };
    participantInfo: ParticipantInfo | [];
    consoleLogs: Array<{}>;
}

function addUserLikeChat(userLikeChatWidgetId: string) {
    if (document.getElementById("userLikeChatScript")) {
        return;
    }
    const userLikeChat = document.createElement("script");
    userLikeChat.setAttribute(
        "src",
        `https://userlike-cdn-widgets.s3-eu-west-1.amazonaws.com/${userLikeChatWidgetId}.js`
    );
    userLikeChat.async = true;
    userLikeChat.id = "userLikeChatScript";
    document.body.appendChild(userLikeChat);
}

export default new Vuex.Store<VibrixStore>({
    state: {
        studio: null,
        user: null,
        snackbar: {
            text: "",
            color: "",
            value: false,
            trigger: false,
            timeout: 5000,
        },
        participantInfo: [],
        userPreferences: null,
        consoleLogs: [],
    },
    mutations: {
        setUser(state, payload: { user: User | null }) {
            state.user = payload.user;
        },
        setUserPreferences(
            state,
            payload: { userPreferences: UserPreferences }
        ) {
            if (state.user) {
                state.userPreferences = payload.userPreferences;
            }
        },
        resetStudio(state) {
            state.studio = null;
            state.participantInfo = [];
        },
        showSnackbar(state, payload: { text: string; color: string }) {
            state.snackbar = {
                ...payload,
                value: true,
                trigger: true,
                timeout: 5000,
            };
        },
        hideSnackbar(state) {
            state.snackbar = { ...state.snackbar, value: false };
        },
        setSnackbarValue(state, payload: { value: boolean }) {
            state.snackbar = { ...state.snackbar, value: payload.value };
        },
        localLayoutUpdated(state, payload: { layout: Layout[] }) {
            if (state.studio) {
                const jitsiIndex = payload.layout.findIndex(
                    x => x.i === "jitsi"
                );
                if (jitsiIndex && jitsiIndex >= 0) {
                    const [jitsi] = payload.layout.splice(jitsiIndex, 1);
                    payload.layout.unshift(jitsi);
                }
                state.studio.layout = payload.layout;
            }
        },
        localAttributesUpdated(
            state,
            payload: {
                attributes: StudioAttributes;
            }
        ) {
            Object.keys(payload.attributes).forEach((key: string) => {
                const attrKey = key as keyof StudioAttributes;
                if (payload.attributes[attrKey] !== undefined && state.studio) {
                    state.studio.attributes[attrKey] =
                        (payload.attributes[attrKey] as string) ?? null;
                }
            });
            if (
                payload.attributes.userLikeChatWidgetId &&
                payload.attributes.userLikeChatWidgetId != ""
            ) {
                addUserLikeChat(payload.attributes.userLikeChatWidgetId);
            }
        },
        addUserScript(state, payload: { script: Script }) {
            if (!state.user) {
                return;
            }
            const index = state.user.scripts.findIndex(
                s => s.id == payload.script.id
            );
            if (index >= 0) {
                state.user.scripts.splice(index, 1);
            }
            state.user.scripts.push(payload.script);
        },
        removeUserScript(state, id: string) {
            if (!state.user) {
                return;
            }
            const index = state.user.scripts.findIndex(s => s.id == id);
            state.user.scripts.splice(index, 1);
        },
        addQuickConnectCode(
            state,
            payload: { code: QuickConnectCode; studioId: string }
        ) {
            if (!state.user?.own_studios) {
                return;
            }
            const studioToUpdate = state.user.own_studios.find(
                (i: { studio: { studioId: string } }) =>
                    i.studio.studioId === payload.studioId
            );
            studioToUpdate?.studio.quick_connect_codes.push(payload.code);
        },
        removeQuickConnectCode(
            state,
            payload: { code: QuickConnectCode; studioId: string }
        ) {
            if (!state.user?.own_studios) {
                return;
            }
            const studioToUpdate = state.user.own_studios.find(
                (i: { studio: { studioId: string } }) =>
                    i.studio.studioId === payload.studioId
            );

            const index: number =
                studioToUpdate?.studio.quick_connect_codes.findIndex(
                    s => s.id == payload.code.id
                ) || 0;
            studioToUpdate?.studio.quick_connect_codes.splice(index, 1);
        },
        addConsoleLog(state, payload) {
            if (payload.payload.length > 1) {
                state.consoleLogs.push({ ...payload });
                ErrorReportService.createReport({ ...payload }, state.user);
            }
        },
    },
    actions: {
        socket_disconnect(context) {
            if (context.state.studio)
                context.state.studio.healthySession = false;
        },
        socket_studioJoined(context, payload: { studio: StudioState }) {
            context.state.studio = { ...payload.studio, healthySession: true };
        },
        socket_attributesUpdated(
            context,
            payload: {
                attributes: StudioAttributes;
            }
        ) {
            if (context.state.studio) {
                context.state.studio.attributes = payload.attributes;
                if (
                    payload.attributes.userLikeChatWidgetId &&
                    payload.attributes.userLikeChatWidgetId != ""
                ) {
                    addUserLikeChat(payload.attributes.userLikeChatWidgetId);
                }
            }
        },
        socket_backgroundUpdated(
            context,
            payload: {
                background: BackgroundSettings | null;
            }
        ) {
            if (context.state.studio) {
                context.state.studio.backgroundSettings = payload.background;
            }
        },
        socket_layoutUpdated(
            context,
            payload: {
                layout: Layout[];
            }
        ) {
            if (context.state.studio) {
                const jitsiIndex = payload.layout.findIndex(
                    x => x.i === "jitsi"
                );
                if (jitsiIndex && jitsiIndex >= 0) {
                    const [jitsi] = payload.layout.splice(jitsiIndex, 1);
                    payload.layout.unshift(jitsi);
                }
                context.state.studio.layout = payload.layout;
            }
        },
        socket_widgetAdded(
            context,
            payload: { widget: WidgetConfiguration<WidgetState, WidgetPayload> }
        ) {
            context.state.studio?.widgetConfig.push(payload.widget);
        },
        socket_widgetRemoved(context, payload: { widgetId: string }) {
            const index = context.state.studio?.widgetConfig.findIndex(
                w => w.widgetId === payload.widgetId
            );
            if (index != null && index >= 0) {
                context.state.studio?.widgetConfig.splice(index, 1);
            }
        },
        socket_widgetUpdated(
            context,
            payload: {
                widgetId: string;
                state: WidgetState;
            }
        ) {
            if (context.state.studio) {
                const widgetConfig = context.state.studio.widgetConfig.find(
                    c => c.widgetId === payload.widgetId
                );

                if (widgetConfig) {
                    widgetConfig.state = payload.state;
                }
            }
        },
        socket_widgetConfigUpdated(
            context,
            payload: {
                widgetConfig: WidgetConfiguration<WidgetState, WidgetPayload>[];
            }
        ) {
            if (context.state.studio) {
                context.state.studio.widgetConfig = payload.widgetConfig;
            }
        },
        socket_errorRaised(context, payload: { error: string }) {
            context.commit("showSnackbar", {
                text: payload.error,
                color: "error",
            });
        },
        socket_showInfo(context, payload: { info: string; color: string }) {
            if (
                !this.getters.isPresenter &&
                (payload.info.includes("joined") ||
                    payload.info.includes("left")) &&
                !context.state.studio?.attributes.showJoinLeave
            ) {
                return;
            } else {
                context.commit("showSnackbar", {
                    text: payload.info,
                    color: payload.color,
                });
            }
        },
        socket_participantInfo(context, payload: ParticipantInfo) {
            context.state.participantInfo = payload;
        },
    },
    getters: {
        isPresenter: state => {
            let isOwnStudio: boolean = false;

            if (state.studio && state.user) {
                const studioId = state.studio.studioId;
                const ownStudio = state.user.own_studios.filter(s => {
                    return s.studio.studioId == studioId;
                });
                if (ownStudio.length >= 1) {
                    isOwnStudio = true;
                }
            } else if (!state.studio && state.user) {
                return true;
            }
            if (!state.user || state.user.is_guest || !isOwnStudio) {
                return false;
            }

            return true;
        },
    },
    modules: {},
});
